import Vue from "vue";
import { createChannel, deleteChannel, getTeamGramPipelines, listChannels, updateChannel } from "../../api/channel";
import { PERMISSION_TYPES } from "../../language/permissionTypes";

const channel = {
    namespaced: true,
    state: {
        channels: [],
    },
    getters: {
        whatsappBusinessChannelExists: (state) => {
            return !!state.channels.find((x) => x.type === "whatsapp-business-api" && x.enabled);
        },
        anyWhatsappChannelExists: (state) => {
            return !!state.channels.find((x) => x.type.startsWith("whatsapp") && x.enabled);
        },

        getVisibleWhatsappChannels: (state, getters, rootState, rootGetters) => {
            const checkPermission = rootGetters["auth/checkPermission"];
            const filteredChannels = state.channels.filter((x) => {
                return ["whatsapp-qr", "whatsapp-v2", "whatsapp-business-api"].includes(x.type);
            });
            return filteredChannels.filter((x) => {
                const user = rootState.auth.user;
                const adminUser = rootState.auth.adminUser;
                if (adminUser) return true;
                const userId = user?._id;
                const userDepartments = user?.subjects || [];
                const visibilityUsers = x.visibility.users || [];
                const visibilityDepartments = x.visibility.departments || [];
                const departmentMatch = userDepartments.some((department) =>
                    visibilityDepartments.includes(department)
                );
                const userMatch = visibilityUsers.includes(userId);
                const isPublic = visibilityUsers.length === 0 && visibilityDepartments.length === 0;

                return (
                    isPublic ||
                    userMatch ||
                    departmentMatch ||
                    checkPermission(PERMISSION_TYPES["integrations.whatsapp.view"].tr.name)
                );
            });
        },
        getVisibleInstagramChannels: (state, getters, rootState, rootGetters) => {
            const checkPermission = rootGetters["auth/checkPermission"];
            const filteredChannels = state.channels.filter((x) => {
                return ["instagram"].includes(x.type);
            });
            return filteredChannels.filter((x) => {
                const user = rootState.auth.user;
                const adminUser = rootState.auth.adminUser;
                if (adminUser) return true;
                const userId = user?._id;
                const userDepartments = user?.subjects || [];
                const visibilityUsers = x.visibility.users || [];
                const visibilityDepartments = x.visibility.departments || [];
                const departmentMatch = userDepartments.some((department) =>
                    visibilityDepartments.includes(department)
                );
                const userMatch = visibilityUsers.includes(userId);
                const isPublic = visibilityUsers.length === 0 && visibilityDepartments.length === 0;

                return (
                    isPublic ||
                    userMatch ||
                    departmentMatch ||
                    checkPermission(PERMISSION_TYPES["integrations.whatsapp.view"].tr.name) //todo permission fix
                );
            });
        },
        getVisibleMessengerChannels: (state, getters, rootState, rootGetters) => {
            const checkPermission = rootGetters["auth/checkPermission"];
            const filteredChannels = state.channels.filter((x) => {
                return ["messenger"].includes(x.type);
            });
            return filteredChannels.filter((x) => {
                const user = rootState.auth.user;
                const adminUser = rootState.auth.adminUser;
                if (adminUser) return true;
                const userId = user?._id;
                const userDepartments = user?.subjects || [];
                const visibilityUsers = x.visibility.users || [];
                const visibilityDepartments = x.visibility.departments || [];
                const departmentMatch = userDepartments.some((department) =>
                    visibilityDepartments.includes(department)
                );
                const userMatch = visibilityUsers.includes(userId);
                const isPublic = visibilityUsers.length === 0 && visibilityDepartments.length === 0;

                return (
                    isPublic ||
                    userMatch ||
                    departmentMatch ||
                    checkPermission(PERMISSION_TYPES["integrations.whatsapp.view"].tr.name) //todo permission fix
                );
            });
        },
        getVisibleV1Channels: (state, getters) => {
            return getters.getVisibleWhatsappChannels.filter((x) => x.type === "whatsapp-qr");
        },
        getVisibleV2Channels: (state, getters) => {
            return getters.getVisibleWhatsappChannels.filter((x) => x.type === "whatsapp-v2");
        },
        getVisibleBusinessChannels: (state, getters) => {
            return getters.getVisibleWhatsappChannels.filter((x) => x.type === "whatsapp-business-api");
        },
        isChannelVisibleForMe: (state, getters, rootState, rootGetters) => (channel) => {
            const user = rootState.auth.user;
            const adminUser = rootState.auth.adminUser;
            if (adminUser) return true;
            const userId = user?._id;
            const userDepartments = user?.subjects || [];
            const visibilityUsers = channel?.visibility?.users || [];
            const visibilityDepartments = channel?.visibility?.departments || [];
            const departmentMatch = userDepartments.some((department) => visibilityDepartments.includes(department));
            const userMatch = visibilityUsers.includes(userId);
            const isPublic = visibilityUsers.length === 0 && visibilityDepartments.length === 0;
            const checkPermission = rootGetters["auth/checkPermission"];
            return (
                isPublic ||
                userMatch ||
                departmentMatch ||
                checkPermission(PERMISSION_TYPES["integrations.whatsapp.view"].tr.name)
            );
        },
    },
    mutations: {
        ADD_CHANNEL: (state, channel) => {
            state.channels = [...state.channels, channel];
        },
        SET_CHANNELS: (state, channels) => {
            state.channels = channels;
        },
        UPDATE_CHANNEL: (state, payload) => {
            let index = state.channels.findIndex((p) => p._id === payload._id);
            if (index === -1) {
                Vue.set(state.channels, index, {
                    ...state.channels[index],
                    ...payload,
                });
            }
        },
        DELETE_CHANNEL: (state, channelId) => {
            state.channels = state.channels.filter((p) => p._id !== channelId);
        },
    },
    actions: {
        listChannels({ commit }) {
            return listChannels()
                .then((response) => {
                    commit("SET_CHANNELS", response.data);
                    return response.data;
                })
                .catch((error) => {
                    console.log("listChannels error...", error);
                });
        },
        getChannel({ state }, id) {
            return state.channels.find((p) => p._id === id);
        },
        createChannel({ commit }, payload) {
            return createChannel(payload.type, payload.name, payload.payload, payload.visibility, payload.forward).then(
                (response) => {
                    commit("ADD_CHANNEL", response.data);
                    return response.data;
                }
            );
        },
        updateChannel({ commit }, payload) {
            return updateChannel(
                payload._id,
                payload.name,
                payload.enabled,
                payload.payload,
                payload.visibility,
                payload.forward,
                payload.commentForward
            ).then((response) => {
                const isSuccess = response.data;
                if (isSuccess) {
                    commit("UPDATE_CHANNEL", payload);
                }
                return isSuccess;
            });
        },
        deleteChannel({ commit }, channelId) {
            return deleteChannel(channelId).then((response) => {
                const isSuccess = response.data;
                if (isSuccess) {
                    commit("DELETE_CHANNEL", channelId);
                }
                return isSuccess;
            });
        },
        getTeamGramPipelines({ commit }, channelId) {
            return getTeamGramPipelines(channelId).then((response) => {
                return response.data;
            });
        },
    },
};

export default channel;
